/**
 * Header Interactive Functionality
 * Online Store 2.0 Theme
 */

class HeaderManager {
  constructor() {
    this.header = document.querySelector('[data-header]');
    this.mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle]');
    this.mobileDrawer = document.querySelector('[data-mobile-drawer]');
    this.mobileDrawerOverlay = document.querySelector('[data-mobile-drawer-overlay]');
    this.mobileDrawerClose = document.querySelector('[data-mobile-drawer-close]');
    this.searchContainer = document.querySelector('[data-search-container]');
    this.searchToggle = document.querySelector('[data-search-toggle]');
    this.searchForm = document.querySelector('[data-search-form]');
    this.searchInput = document.querySelector('[data-search-input]');
    this.searchResults = document.querySelector('[data-search-results]');
    
    this.isDrawerOpen = false;
    this.isSearchOpen = false;
    this.searchTimeout = null;
    this.focusableElements = [];
    this.lastFocusedElement = null;
    
    this.init();
  }

  init() {
    this.setupStickyHeader();
    this.setupMobileMenu();
    this.setupMegaMenus();
    this.setupSearch();
    this.setupKeyboardNavigation();
    this.setupResizeHandler();
  }

  /**
   * Sticky Header Functionality
   */
  setupStickyHeader() {
    if (!this.header || !this.header.classList.contains('header--sticky')) return;

    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > 100) {
        this.header.classList.add('header--scrolled');
      } else {
        this.header.classList.remove('header--scrolled');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick, { passive: true });
  }

  /**
   * Mobile Menu Functionality
   */
  setupMobileMenu() {
    if (!this.mobileMenuToggle || !this.mobileDrawer) return;

    // Toggle mobile menu
    this.mobileMenuToggle.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleMobileDrawer();
    });

    // Close on overlay click
    if (this.mobileDrawerOverlay) {
      this.mobileDrawerOverlay.addEventListener('click', () => {
        this.closeMobileDrawer();
      });
    }

    // Close button
    if (this.mobileDrawerClose) {
      this.mobileDrawerClose.addEventListener('click', () => {
        this.closeMobileDrawer();
      });
    }

    // Setup accordion functionality
    this.setupMobileAccordions();

    // Handle escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isDrawerOpen) {
        this.closeMobileDrawer();
      }
    });

    // Handle back button (mobile)
    window.addEventListener('popstate', () => {
      if (this.isDrawerOpen) {
        this.closeMobileDrawer();
      }
    });
  }

  setupMobileAccordions() {
    const accordionTriggers = document.querySelectorAll('[data-mobile-accordion-trigger]');
    
    accordionTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const content = document.getElementById(trigger.getAttribute('aria-controls'));
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        
        // Close all other accordions
        accordionTriggers.forEach(otherTrigger => {
          if (otherTrigger !== trigger) {
            otherTrigger.setAttribute('aria-expanded', 'false');
            const otherContent = document.getElementById(otherTrigger.getAttribute('aria-controls'));
            if (otherContent) {
              otherContent.classList.remove('is-open');
              otherContent.style.maxHeight = '0px';
            }
          }
        });
        
        // Toggle current accordion
        trigger.setAttribute('aria-expanded', !isExpanded);
        if (content) {
          content.classList.toggle('is-open', !isExpanded);
          if (!isExpanded) {
            // Expand: set max-height to scrollHeight for smooth animation
            // Recalculate after potential image load
            const setHeight = () => {
              content.style.maxHeight = content.scrollHeight + 'px';
            };
            setHeight();
            // In case images inside load later and change height
            const imgs = content.querySelectorAll('img');
            imgs.forEach((img) => {
              if (!img.complete) {
                img.addEventListener('load', setHeight, { once: true });
              }
            });
          } else {
            // Collapse
            content.style.maxHeight = '0px';
          }
        }
      });
    });
  }

  toggleMobileDrawer() {
    if (this.isDrawerOpen) {
      this.closeMobileDrawer();
    } else {
      this.openMobileDrawer();
    }
  }

  openMobileDrawer() {
    this.isDrawerOpen = true;
    this.mobileDrawer.classList.add('is-open');
    this.mobileMenuToggle.setAttribute('aria-expanded', 'true');
    this.mobileMenuToggle.setAttribute('aria-label', 'Close menu');
    
    // Prevent body scroll
    document.body.classList.add('mobile-drawer-open');
    
    // Store last focused element
    this.lastFocusedElement = document.activeElement;
    
    // Set up focus trap
    this.setupFocusTrap();
    
    // Focus first focusable element in drawer
    setTimeout(() => {
      const firstFocusable = this.mobileDrawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }, 100);

    // Add history state for back button handling
    if (window.history && window.history.pushState) {
      window.history.pushState({ mobileMenuOpen: true }, '');
    }
  }

  closeMobileDrawer() {
    this.isDrawerOpen = false;
    this.mobileDrawer.classList.remove('is-open');
    this.mobileMenuToggle.setAttribute('aria-expanded', 'false');
    this.mobileMenuToggle.setAttribute('aria-label', 'Open menu');
    
    // Restore body scroll
    document.body.classList.remove('mobile-drawer-open');
    
    // Close all accordions
    const accordionTriggers = document.querySelectorAll('[data-mobile-accordion-trigger]');
    accordionTriggers.forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
      const content = document.getElementById(trigger.getAttribute('aria-controls'));
      if (content) {
        content.classList.remove('is-open');
      }
    });
    
    // Restore focus
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
      this.lastFocusedElement = null;
    }
    
    // Remove focus trap
    this.removeFocusTrap();
  }

  setupFocusTrap() {
    this.focusableElements = this.mobileDrawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (this.focusableElements.length === 0) return;
    
    const firstFocusable = this.focusableElements[0];
    const lastFocusable = this.focusableElements[this.focusableElements.length - 1];
    
    this.trapFocusHandler = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      }
    };
    
    document.addEventListener('keydown', this.trapFocusHandler);
  }

  removeFocusTrap() {
    if (this.trapFocusHandler) {
      document.removeEventListener('keydown', this.trapFocusHandler);
      this.trapFocusHandler = null;
    }
    this.focusableElements = [];
  }

  /**
   * Mega Menu Functionality
   */
  setupMegaMenus() {
    const navItems = document.querySelectorAll('.header__nav-item--has-dropdown');
    
    navItems.forEach(item => {
      const trigger = item.querySelector('.header__nav-link');
      const megaMenu = item.querySelector('.mega-menu');
      let hoverTimeout;
      
      if (!trigger || !megaMenu) return;

      // Mouse events
      item.addEventListener('mouseenter', () => {
        clearTimeout(hoverTimeout);
        hoverTimeout = setTimeout(() => {
          this.openMegaMenu(trigger, megaMenu);
        }, 150);
      });

      item.addEventListener('mouseleave', () => {
        clearTimeout(hoverTimeout);
        hoverTimeout = setTimeout(() => {
          this.closeMegaMenu(trigger, megaMenu);
        }, 150);
      });

      // Keyboard events
      trigger.addEventListener('focus', () => {
        this.openMegaMenu(trigger, megaMenu);
      });

      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeMegaMenu(trigger, megaMenu);
          trigger.focus();
        }
      });

      // Close on outside click
      document.addEventListener('click', (e) => {
        if (!item.contains(e.target)) {
          this.closeMegaMenu(trigger, megaMenu);
        }
      });
    });

    // Close all mega menus on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllMegaMenus();
      }
    });
  }

  openMegaMenu(trigger, megaMenu) {
    // Close other mega menus first
    this.closeAllMegaMenus();
    
    trigger.setAttribute('aria-expanded', 'true');
    megaMenu.style.opacity = '1';
    megaMenu.style.visibility = 'visible';
    megaMenu.style.transform = 'translateY(0)';
    
    // Focus first link in mega menu if opened via keyboard
    if (document.activeElement === trigger) {
      const firstLink = megaMenu.querySelector('a');
      if (firstLink) {
        setTimeout(() => firstLink.focus(), 100);
      }
    }
  }

  closeMegaMenu(trigger, megaMenu) {
    trigger.setAttribute('aria-expanded', 'false');
    megaMenu.style.opacity = '0';
    megaMenu.style.visibility = 'hidden';
    megaMenu.style.transform = 'translateY(0)';
  }

  closeAllMegaMenus() {
    const triggers = document.querySelectorAll('.header__nav-link[aria-expanded="true"]');
    triggers.forEach(trigger => {
      const megaMenu = trigger.parentNode.querySelector('.mega-menu');
      if (megaMenu) {
        this.closeMegaMenu(trigger, megaMenu);
      }
    });
  }

  /**
   * Search Functionality
   */
  setupSearch() {
    if (!this.searchToggle) return;

    this.searchToggle.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleSearch();
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.debounce((e) => {
        this.handleSearchInput(e.target.value);
      }, 300));

      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeSearch();
        }
      });
    }

    // Close search on outside click
    document.addEventListener('click', (e) => {
      if (this.searchContainer && !this.searchContainer.contains(e.target)) {
        this.closeSearch();
      }
    });
  }

  toggleSearch() {
    if (this.isSearchOpen) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  openSearch() {
    if (!this.searchForm) return;
    
    this.isSearchOpen = true;
    this.searchForm.classList.add('is-open');
    
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.focus();
      }
    }, 100);
  }

  closeSearch() {
    if (!this.searchForm) return;
    
    this.isSearchOpen = false;
    this.searchForm.classList.remove('is-open');
    
    if (this.searchResults) {
      this.searchResults.innerHTML = '';
    }
  }

  async handleSearchInput(query) {
    if (!query.trim() || query.length < 2) {
      if (this.searchResults) {
        this.searchResults.innerHTML = '';
      }
      return;
    }

    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article&resources[limit]=8`);
      const data = await response.json();
      
      this.renderSearchResults(data);
    } catch (error) {
      console.error('Search error:', error);
    }
  }

  renderSearchResults(data) {
    if (!this.searchResults) return;

    let html = '';
    
    if (data.resources && data.resources.results) {
      const { products, collections, articles } = data.resources.results;
      
      if (products && products.length > 0) {
        html += '<div class="search-results-section"><h4>Products</h4>';
        products.slice(0, 4).forEach(product => {
          html += `
            <a href="${product.url}" class="search-result-item">
              ${product.featured_image ? `<img src="${product.featured_image.url}" alt="${product.title}" width="40" height="40">` : ''}
              <span>${product.title}</span>
            </a>
          `;
        });
        html += '</div>';
      }
      
      if (collections && collections.length > 0) {
        html += '<div class="search-results-section"><h4>Collections</h4>';
        collections.slice(0, 2).forEach(collection => {
          html += `
            <a href="${collection.url}" class="search-result-item">
              <span>${collection.title}</span>
            </a>
          `;
        });
        html += '</div>';
      }
    }
    
    if (html === '') {
      html = '<div class="search-no-results">No results found</div>';
    }
    
    this.searchResults.innerHTML = html;
  }

  /**
   * Keyboard Navigation
   */
  setupKeyboardNavigation() {
    // Handle tab navigation in mega menus
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        const activeElement = document.activeElement;
        const megaMenu = activeElement.closest('.mega-menu');
        
        if (megaMenu) {
          const focusableElements = megaMenu.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];
          
          if (e.shiftKey && activeElement === firstElement) {
            e.preventDefault();
            const trigger = megaMenu.parentNode.querySelector('.header__nav-link');
            if (trigger) trigger.focus();
          } else if (!e.shiftKey && activeElement === lastElement) {
            e.preventDefault();
            this.closeMegaMenu(
              megaMenu.parentNode.querySelector('.header__nav-link'),
              megaMenu
            );
          }
        }
      }
    });
  }

  /**
   * Resize Handler
   */
  setupResizeHandler() {
    let resizeTimeout;
    
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Close mobile drawer on resize to desktop
        if (window.innerWidth > 1024 && this.isDrawerOpen) {
          this.closeMobileDrawer();
        }
        
        // Close search on mobile
        if (window.innerWidth <= 768 && this.isSearchOpen) {
          this.closeSearch();
        }
        
        // Close mega menus on resize
        this.closeAllMegaMenus();
      }, 250);
    });
  }

  /**
   * Utility Functions
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

/**
 * Cart Badge Update
 */
class CartBadgeManager {
  constructor() {
    this.badge = document.querySelector('.header__cart-badge');
    this.init();
  }

  init() {
    // Listen for cart updates
    document.addEventListener('cart:updated', (e) => {
      this.updateBadge(e.detail.itemCount);
    });

    // Listen for Shopify cart changes (if using Ajax API)
    if (window.Shopify && window.Shopify.onCartUpdate) {
      const originalUpdate = window.Shopify.onCartUpdate;
      window.Shopify.onCartUpdate = (cart) => {
        this.updateBadge(cart.item_count);
        if (originalUpdate) originalUpdate(cart);
      };
    }
  }

  updateBadge(count) {
    if (!this.badge) return;

    if (count > 0) {
      this.badge.textContent = count;
      this.badge.style.display = 'flex';
      
      // Update aria-label on cart link
      const cartLink = this.badge.closest('.header__action--cart');
      if (cartLink) {
        cartLink.setAttribute('aria-label', `Cart (${count} items)`);
      }
    } else {
      this.badge.style.display = 'none';
      
      // Update aria-label on cart link
      const cartLink = this.badge.closest('.header__action--cart');
      if (cartLink) {
        cartLink.setAttribute('aria-label', 'Cart (empty)');
      }
    }
  }
}

/**
 * Initialize Header Functionality
 */
document.addEventListener('DOMContentLoaded', () => {
  new HeaderManager();
  new CartBadgeManager();
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { HeaderManager, CartBadgeManager };
}
